"use client";

import { useEffect, useState } from "react";
import {
  useActiveOrganization,
  useSession,
  useDeployment,
  useOrganizationMemberships,
  useWorkspaceList,
  useActiveTenancy,
} from "@wacht/tanstack-router";
import { WorkspaceSelectorMenu } from "@/components/organization/workspace-selector-menu";
import { CreateOrganizationForm } from "@/components/organization/create-organization-form";
import FullscreenLoader from "@/components/fullscreen-loader";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

interface RequireActiveTenancyProps {
  children: React.ReactNode;
}

export const CustomRequireActiveTenancy = ({
  children,
}: RequireActiveTenancyProps) => {
  const { loading: sessionLoading, session, switchOrganization } = useSession();
  const { activeOrganization } = useActiveOrganization();
  
  // Safely get active tenancy data - use optional chaining to prevent errors
  const tenancyResult = useActiveTenancy();
  const workspaceMembership = tenancyResult?.workspaceMembership;
  const activeWorkspace = workspaceMembership?.workspace;
  
  // Safely get workspaces - handle both array and object with data property
  const workspaceListResult:any = useWorkspaceList();
  // Handle case where hook might return data directly or in a nested structure
  const workspacesData:any = workspaceListResult?.workspaces ?? workspaceListResult?.data ?? workspaceListResult;
  const workspacesLoading = workspaceListResult?.loading ?? false;
  
  // Extract data array from response if needed, ensure workspaces is always an array
  let workspaces: any[] = [];
  if (workspacesData !== undefined && workspacesData !== null) {
    if (Array.isArray(workspacesData)) {
      workspaces = workspacesData;
    } else if (typeof workspacesData === 'object') {
      // Try to extract from data property
      if (workspacesData.data !== undefined && Array.isArray(workspacesData.data)) {
        workspaces = workspacesData.data;
      }
    }
  }
  
  const { deployment } = useDeployment();
  
  // Safely get organization memberships - handle both array and object with data property
  const orgMembershipsResult:any = useOrganizationMemberships();
  // Handle case where hook might return data directly or in a nested structure
  const orgMembershipsData = orgMembershipsResult?.organizationMemberships ?? orgMembershipsResult?.data ?? orgMembershipsResult;
  const organizationMembershipsLoading = orgMembershipsResult?.loading ?? false;
  
  // Extract data array from response if needed, ensure organizationMemberships is always an array or undefined
  let organizationMemberships: any[] | undefined = undefined;
  if (orgMembershipsData !== undefined && orgMembershipsData !== null) {
    if (Array.isArray(orgMembershipsData)) {
      organizationMemberships = orgMembershipsData;
    } else if (typeof orgMembershipsData === 'object' && orgMembershipsData !== null) {
      // Try to extract from data property
      if (orgMembershipsData.data !== undefined && Array.isArray(orgMembershipsData.data)) {
        organizationMemberships = orgMembershipsData.data;
      }
    }
  }
  
  const [showCreateOrg, setShowCreateOrg] = useState(false);

  const workspacesEnabled =
    deployment?.b2b_settings.workspaces_enabled ?? false;

  // Check if user has no organizations - ensure safe length access
  const hasNoOrganizations =
    !organizationMembershipsLoading &&
    (!organizationMemberships || 
     (Array.isArray(organizationMemberships) && 
      organizationMemberships.length !== undefined && 
      organizationMemberships.length === 0));

  // Auto-select first organization if it exists and user doesn't have an active one
  useEffect(() => {
    if (
      !sessionLoading &&
      !organizationMembershipsLoading &&
      organizationMemberships &&
      Array.isArray(organizationMemberships) &&
      organizationMemberships.length > 0 &&
      !activeOrganization
    ) {
      const firstOrg = organizationMemberships[0]?.organization;
      if (firstOrg?.id) {
        switchOrganization(firstOrg.id).catch((error: any) => {
          console.error("Failed to auto-select organization:", error);
        });
      }
    }
  }, [
    sessionLoading,
    organizationMembershipsLoading,
    organizationMemberships,
    activeOrganization,
    switchOrganization,
  ]);

  // Show create org dialog if user has no organizations
  useEffect(() => {
    if (hasNoOrganizations && !showCreateOrg) {
      setShowCreateOrg(true);
    }
  }, [hasNoOrganizations, showCreateOrg]);

  const handleOrganizationCreated = async (organization?: any) => {
    // Extract organization ID from response (could be nested)
    const orgId =
      organization?.id ||
      (organization as any)?.data?.organization?.id ||
      (organization as any)?.organization?.id;

    setShowCreateOrg(false);
    
    if (orgId) {
      // Auto-select the newly created organization
      try {
        await switchOrganization(orgId);
        // The organizationMemberships will automatically refresh via the hook
      } catch (error: any) {
        console.error("Failed to switch to new organization:", error);
        // Even if switching fails, the organization was created
        // The useEffect will handle auto-selection once memberships are loaded
      }
    }
    // If we can't get the ID from the response, wait for memberships to refresh
    // and auto-select the first one - the useEffect will handle this
  };

  // Check if user has restrictions on current memberships
  const activeOrgMembership = Array.isArray(organizationMemberships)
    ? organizationMemberships.find(
        (m: any) => m?.organization?.id === activeOrganization?.id,
      )
    : undefined;
  const activeWorkspaceMembership = workspaces.find(
    (w: any) => w?.id === session?.active_signin?.active_workspace_membership_id,
  );

  const hasOrgRestriction =
    activeOrgMembership?.eligibility_restriction?.type !== "none" &&
    activeOrgMembership?.eligibility_restriction?.type !== undefined;

  const hasWorkspaceRestriction =
    activeWorkspaceMembership?.eligibility_restriction?.type !== "none" &&
    activeWorkspaceMembership?.eligibility_restriction?.type !== undefined;

  // Wait for initial data to load - show loader when workspaces are loading
  // Also wait a bit after loading completes to ensure data is processed
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  
  useEffect(() => {
    // Reset if loading starts again
    if (sessionLoading || organizationMembershipsLoading || (workspacesEnabled && workspacesLoading)) {
      setHasInitiallyLoaded(false);
      return;
    }
    
    // Small delay to ensure data is fully processed after loading completes
    const timer = setTimeout(() => {
      setHasInitiallyLoaded(true);
    }, 150);
    return () => clearTimeout(timer);
  }, [sessionLoading, organizationMembershipsLoading, workspacesLoading, workspacesEnabled]);

  if (
    sessionLoading ||
    organizationMembershipsLoading ||
    (workspacesEnabled && workspacesLoading) ||
    !hasInitiallyLoaded
  ) {
    return <FullscreenLoader />;
  }

  // If user has no organizations, show create organization form
  if (hasNoOrganizations) {
    return (
      <CreateOrganizationForm
        isOpen={showCreateOrg}
        onClose={() => setShowCreateOrg(false)}
        onSuccess={handleOrganizationCreated}
      />
    );
  }

  // Check if user has valid active tenancy
  const hasValidTenancy = () => {
    // If user has restrictions, they need to switch
    if (hasOrgRestriction || hasWorkspaceRestriction) {
      return false;
    }

    // Must have an active organization
    if (!activeOrganization) {
      return false;
    }

    // If workspaces are disabled, org is enough
    if (!workspacesEnabled) {
      return true;
    }

    // If workspaces enabled, must have active workspace
    if (!activeWorkspace) {
      return false;
    }

    // Workspace must belong to the active org
    const workspaceBelongsToOrg = workspaces.some(
      (w: any) =>
        w?.id === activeWorkspace.id &&
        w?.organization?.id === activeOrganization.id,
    );

    return workspaceBelongsToOrg;
  };

  // If valid tenancy, render the app
  if (hasValidTenancy()) {
    return <>{children}</>;
  }

  // Otherwise, show the workspace selector menu
  // Organization is already auto-selected, so we only need workspace selection
  return (
    <Dialog open={true}>
      <DialogContent
        className="p-0 max-w-[95vw] w-full"
        style={{ maxWidth: "1000px", width: "100%" }}
        showCloseButton={false}
      >
        <WorkspaceSelectorMenu />
      </DialogContent>
    </Dialog>
  );
};

